// Bir yöneticinin doğrudan raporları: /me/team sayfası ve personel sayfasının
// "Ekibi" bölümü aynı tabloyu çizer.
//
// Durum sütunu çalışanın yaşam döngüsü durumudur (aktif / izinde / …), "bugün
// izinde mi" değil. İzin listesi tarih süzgeci almıyor ve en eskiden sıralıyor;
// bugünün iznini bulmak için her kişinin bütün geçmişini sayfalamak gerekirdi.
// O yüzden çizimdeki ofiste/uzaktan/izinde göstergesi, kişi başı izin bakiyesi
// ve bekleyen talep rozeti çizilmedi.

import { useTranslation } from "react-i18next";

import {
  Avatar,
  Badge,
  Card,
  DataTable,
  type DataTableColumn,
} from "@novemai/components";

import { useKeysetQuery } from "@/api/keyset";
import { PersonLink } from "@/components/person-link";
import { formatDate, formatTenureDuration } from "@/lib/date-range";

import { fetchDirectReportsPage, type EmployeeRow } from "./api";
import { TabQueryState } from "./detail-tab-state";
import { fullName, initialsOf, LIFECYCLE_KEY, LIFECYCLE_TONE } from "./employee-display";

export function DirectReportsTable({
  managerId,
}: {
  readonly managerId: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  const query = useKeysetQuery<EmployeeRow>({
    queryKey: ["employees", "direct-reports", managerId],
    fetchPage: (cursor) => fetchDirectReportsPage(managerId, cursor),
  });

  const columns: readonly DataTableColumn<EmployeeRow>[] = [
    {
      id: "person",
      header: t("people.column.person"),
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={fullName(row)} initials={initialsOf(row)} />
          <div>
            <div className="whitespace-nowrap font-serif text-[14.5px] text-[var(--fg-primary)]">
              <PersonLink employeeId={row.employee_id}>{fullName(row)}</PersonLink>
            </div>
            <div className="whitespace-nowrap font-mono text-[11px] text-[var(--fg-muted)]">
              {row.employee_number ?? none}
              {row.start_date !== null
                ? ` · ${formatTenureDuration(i18n.language, t, row.start_date)}`
                : ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "title",
      header: t("people.detail.field_position"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-secondary)]">
          {row.job_title ?? t("people.cell.no_title")}
        </span>
      ),
    },
    {
      id: "department",
      header: t("people.column.department"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-secondary)]">{row.department_name ?? none}</span>
      ),
    },
    {
      id: "start",
      header: t("people.column.start_date"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-muted)]">
          {row.start_date !== null ? formatDate(i18n.language, row.start_date) : none}
        </span>
      ),
    },
    {
      id: "lifecycle",
      header: t("people.column.lifecycle"),
      cell: (row): React.ReactNode => (
        <Badge tone={LIFECYCLE_TONE[row.lifecycle_state]}>
          {t(LIFECYCLE_KEY[row.lifecycle_state])}
        </Badge>
      ),
    },
  ];

  return (
    <TabQueryState
      query={query}
      isEmpty={query.items.length === 0}
      emptyTitle={t("people.team.empty_title")}
      emptyDescription={t("people.team.empty_description")}
    >
      <Card className="overflow-hidden">
        <DataTable<EmployeeRow>
          caption={t("people.team.caption")}
          columns={columns}
          rows={query.items}
          rowKey={(row): string => row.employee_id}
          hasNextPage={query.hasNextPage}
          onLoadMore={query.fetchNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          loadMoreLabel={t("people.detail.tabs.load_more")}
        />
      </Card>
    </TabQueryState>
  );
}
